import { AvertissementDroitsImage } from '../../composants/AvertissementDroitsImage.js';
import { DepotsRecus } from '../espace-deposant/DepotsRecus.js';
import { BoutonEtiquettes } from './ImpressionEtiquettes.js';
import { CatalogageBloc } from '../catalogage/CatalogageBloc.js';
import { EchangesBloc } from '../boite-reception/EchangesBloc.js';
import { ConseilsConservation } from '../espace-collectionneur/ConseilsConservation.js';
import { EntreeNouvelleAffaire } from '../pipeline/EntreesPipeline.js';
import { ListeAttente } from '../pipeline/ListeAttente.js';
import { APrevenirOeuvre } from '../profils/APrevenirOeuvre.js';
import { PlancherOeuvre } from '../presentation/PlancherOeuvre.js';
import { BoutonCalculette } from '../presentation/CalculetteNegociation.js';
import { AssuranceTab } from './tabs/AssuranceTab.js';
import { LocationsOeuvre } from '../locations/LocationsLiees.js';
import { AjouterASalleDialog } from '../viewing-rooms/AjouterASalleDialog.js';
import { IndicateurFraicheur } from '../racine/IndicateurFraicheur.js';
import { DiffusionTab } from './tabs/DiffusionTab.js';
import {
  Badge,
  Button,
  classesBouton,
  Dialog,
  Dimensions,
  DropdownMenu,
  InventoryNumber,
  Price,
  Select,
  Tabs,
  Textarea,
  type DropdownMenuEntry,
  type TabSpec,
  VisuallyHidden,
} from '@artosera/ui';
import {
  formatDate,
  STATUTS_OEUVRE,
  transitionsPossibles,
  type ChampManquant,
  type StatutOeuvre,
} from '@artosera/shared';
import {
  ChevronDown,
  FileText,
  Image as ImageIcon,
  MapPin,
  Maximize2,
  MoreHorizontal,
} from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { estApiError } from '../../api/errors.js';
import { useArchiverOeuvre, useChangerStatutOeuvre, useOeuvre } from '../../api/oeuvres.js';
import { useApercuMedia } from '../../api/medias.js';
import { documentsGalerieApi } from '../../api/documentsGalerie.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { useFairMode } from '../shell/hooks.js';
import { ProvenanceTab } from './tabs/ProvenanceTab.js';
import { CertificatsTab } from './tabs/CertificatsTab.js';
import { EditionsTab } from './tabs/EditionsTab.js';
import { HistoriqueTab } from './tabs/HistoriqueTab.js';
import { InformationsTab } from './tabs/InformationsTab.js';
import { MarquerVendueDialog } from './MarquerVendueDialog.js';
import { RetourGeste } from '../RetourGeste.js';
import { NouvelleFactureDialog } from '../ventes/NouvelleFactureDialog.js';
import { LocalisationTab } from './tabs/LocalisationTab.js';
import { MediasTab } from './tabs/MediasTab.js';
import { PrixTab } from './tabs/PrixTab.js';
import { PastilleStatut } from '../../composants/PastilleStatut.js';
import { Field } from '../Field.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { NouvelleOffreDialog } from '../offres/NouvelleOffreDialog.js';
import { RangementOeuvre } from '../inventaire/RangementOeuvre.js';
import { OffresOeuvre } from '../offres/HistoriqueContact.js';
import styles from './OeuvreDetailPage.module.css';
import { LeverReservationOeuvre } from './LeverReservationOeuvre.js';
import { LibelleOnglet } from '../module/LectureSeule.js';

const CHAMPS_A_COMPLETER: Record<ChampManquant, { onglet: string; id: string }> = {
  mediaPrincipalId: { onglet: 'medias', id: 'media-depot' },
  creditPhoto: { onglet: 'medias', id: 'media-credit-principal' },
  technique: { onglet: 'informations', id: 'info-technique-fr' },
  dimensions: { onglet: 'informations', id: 'info-hauteur' },
  annee: { onglet: 'informations', id: 'info-annee' },
  'description.fr': { onglet: 'informations', id: 'info-description-fr' },
  'description.en': { onglet: 'informations', id: 'info-description-en' },
  prixPublicCentimes: { onglet: 'prix', id: 'prix-public' },
};

/** Chaque onglet se pose sur la même surface, comme une fiche de la réserve. */
function dansUnPanneau(onglets: readonly TabSpec[]): TabSpec[] {
  return onglets.map((onglet) => ({
    ...onglet,
    content: (
      <div className={styles.panneau} data-ui="artwork-panel">
        {onglet.content}
      </div>
    ),
  }));
}

/**
 * Fiche œuvre complète (3.3, écran 2, `/:slug/oeuvres/:id`) : en-tête image en
 * grand + cartel, onglets, quatre gestes (D5) — seuls « Changer le statut » et
 * « Archiver » sont branchés à ce stade ; les trois autres restent visibles,
 * désactivés, avec l'infobulle « Disponible en finalisation ».
 */
export function OeuvreDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'oeuvres', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { galerieSlug, galerie, compte } = useShellContext();
  const foire = useFairMode();
  const [params] = useSearchParams();

  const fiche = useOeuvre(galerieSlug, id ?? null);
  useEffect(() => {
    const champ = params.get('champ');
    if (champ) document.getElementById(champ)?.focus();
  }, [params, fiche.data]);
  const mediaPrincipal = fiche.data?.medias.find(
    (media) => media.id === fiche.data.oeuvre.mediaPrincipalId,
  );
  const apercu = useApercuMedia(galerieSlug, mediaPrincipal?.id ?? null);

  const changerStatut = useChangerStatutOeuvre(galerieSlug);
  const archiver = useArchiverOeuvre(galerieSlug);

  const [offreOuverte, setOffreOuverte] = useState(false);
  const [correctionOuverte, setCorrectionOuverte] = useState(false);
  const [statutVise, setStatutVise] = useState<StatutOeuvre | ''>('');
  const [raison, setRaison] = useState('');
  const [erreurStatut, setErreurStatut] = useState<string | null>(null);
  const [archivageOuvert, setArchivageOuvert] = useState(false);
  const [fichePdfEnCours, setFichePdfEnCours] = useState(false);
  const [erreurFichePdf, setErreurFichePdf] = useState<string | null>(null);
  const [venteSimpleOuverte, setVenteSimpleOuverte] = useState(false);
  const [factureOuverte, setFactureOuverte] = useState(false);
  const [retourGeste, setRetourGeste] = useState<string | null>(null);
  const [salleOuverte, setSalleOuverte] = useState(false);

  if (fiche.isLoading) {
    return <p className={styles.vide}>{t('commun:chargement')}</p>;
  }
  if (fiche.isError || !fiche.data) {
    return (
      <p className={styles.vide} role="alert">
        {estApiError(fiche.error) ? fiche.error.message : t('commun:erreurReseau')}
      </p>
    );
  }

  const { oeuvre } = fiche.data;
  const ligneTechnique = [oeuvre.annee, oeuvre.technique?.[langue] ?? oeuvre.technique?.fr]
    .filter(Boolean)
    .join(', ');
  const voitProfils =
    galerieAModule(galerie, 'profils-collectionneurs') && galerieALaction(galerie, 'profils:read');
  const ongletProfils =
    voitProfils && params.get('onglet') === 'a-prevenir' ? 'a-prevenir' : undefined;
  const peutEcrire = galerieALaction(galerie, 'artworks:write');
  const peutArchiver = galerieALaction(galerie, 'artworks:archive');
  // Certificats : module `documents-prestige` (D21). Sans lui, l'onglet
  // n'est pas proposé ; la fiche PDF du cœur reste là.
  const peutVoirCertificats =
    galerieALaction(galerie, 'documents:read') && galerieAModule(galerie, 'documents-prestige');
  // Éditions : module `editions-provenance` (D21). Sans lui, une édition
  // existante reste lisible ; on n'en crée pas et on n'engendre pas ses
  // exemplaires.
  const editionsOuvertes = galerieAModule(galerie, 'editions-provenance');
  let ongletInitial = ongletProfils ?? 'informations';
  const ongletDemande = params.get('onglet');
  if (ongletDemande === 'medias' || ongletDemande === 'prix') ongletInitial = ongletDemande;
  if (
    ongletDemande === 'provenance' &&
    editionsOuvertes &&
    galerieALaction(galerie, 'editions:read')
  )
    ongletInitial = 'provenance';
  if (
    ongletDemande === 'diffusion' &&
    galerieAModule(galerie, 'artsy-artnet') &&
    galerieALaction(galerie, 'diffusion:read')
  )
    ongletInitial = 'diffusion';
  // Vente : « Marquer vendue » (cœur, sans facture) quand la table des
  // transitions l'admet. Une édition suivie se vend par exemplaire, par une
  // facture : pas par ce geste.
  const facturationOuverte = galerieAModule(galerie, 'facturation');
  // « Facturer » ouvre une facture : il faut le module ET le droit d'en écrire.
  const peutFacturer = facturationOuverte && galerieALaction(galerie, 'invoices:write');
  const venteSimplePossible =
    peutEcrire &&
    transitionsPossibles(oeuvre.statut).includes('vente_simple') &&
    !(oeuvre.type === 'edition' && oeuvre.tirage?.exemplairesSuivis === true);
  // Mouvements, offres et viewing rooms : actions des modules livrés.
  const mouvementsOuverts =
    galerieAModule(galerie, 'mouvements') && galerieALaction(galerie, 'movements:write');
  const peutAjouterASalle =
    galerieAModule(galerie, 'viewing-rooms') && galerieALaction(galerie, 'viewing-rooms:write');
  // Droits visibles (ARTO-53, § 4) : un seul helper, `galerieALaction`, pour
  // chaque geste d'écriture — jamais de condition dispersée sur le rôle.
  const peutEcrireMedia = galerieALaction(galerie, 'media:write');
  // Écriture partagée avec le cœur : le module des certificats en lecture seule la ferme.
  const peutEmettreCertificat =
    galerieALaction(galerie, 'documents:write') &&
    galerieEcritModule(galerie, 'documents-prestige');

  const entreesStatut: DropdownMenuEntry[] = [
    ...transitionsPossibles(oeuvre.statut)
      // « Facturer » appartient au module `facturation` (D21) ; la vente
      // simple ouvre son propre dialogue (acheteur, prix, date).
      .filter(
        (evenement) =>
          !['location_debut', 'location_fin', 'reservation', 'reservation_expiree'].includes(
            evenement,
          ),
      )
      .filter((evenement) => evenement !== 'archivage' || peutArchiver)
      .filter(
        (evenement) => evenement !== 'facture_emise' || (peutFacturer && oeuvre.statut !== 'louee'),
      )
      .filter((evenement) => !mouvementsOuverts || !['depot', 'pret', 'retour'].includes(evenement))
      .filter((evenement) => evenement !== 'vente_simple' || venteSimplePossible)
      .map((evenement): DropdownMenuEntry => ({
        type: 'item',
        item: {
          key: evenement,
          label:
            evenement === 'vente_simple' && facturationOuverte
              ? t('regie:oeuvres.venteSimple.actionSecondaire')
              : t(`regie:oeuvres.statutMenu.evenements.${evenement}`),
          onSelect: () => {
            if (evenement === 'vente_simple') {
              setVenteSimpleOuverte(true);
              return;
            }
            // « Facturer » ne marque pas l'œuvre vendue d'un clic : il ouvre
            // la facture de cette œuvre. L'œuvre passe « vendue » quand la
            // facture est émise, avec son numéro et ses mentions légales.
            if (evenement === 'facture_emise') {
              setFactureOuverte(true);
              return;
            }
            changerStatut.mutate({
              id: oeuvre.id,
              corps: { evenement },
              statutOptimiste: evenement === 'archivage' ? 'archivee' : oeuvre.statut,
            });
          },
        },
      })),
    { type: 'separator' },
    {
      type: 'item',
      item: {
        key: 'correction',
        label: t('regie:oeuvres.statutMenu.correctionManuelle'),
        onSelect: () => {
          setStatutVise('');
          setRaison('');
          setErreurStatut(null);
          setCorrectionOuverte(true);
        },
      },
    },
  ];

  async function confirmerCorrection(): Promise<void> {
    if (!statutVise) return;
    setErreurStatut(null);
    try {
      await changerStatut.mutateAsync({
        id: oeuvre.id,
        corps: { statut: statutVise, raison },
        statutOptimiste: statutVise,
      });
      setCorrectionOuverte(false);
    } catch (error) {
      setErreurStatut(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  async function ouvrirFichePdf(langueDocument: 'fr' | 'en'): Promise<void> {
    setErreurFichePdf(null);
    setFichePdfEnCours(true);
    try {
      const document = await dansLaPorteeCourante((appel) =>
        documentsGalerieApi.fichePdf(galerieSlug, oeuvre.id, langueDocument, appel),
      );
      ouvrirUrlSignee(document.url);
    } catch (error) {
      setErreurFichePdf(estApiError(error) ? error.message : t('commun:erreurReseau'));
    } finally {
      setFichePdfEnCours(false);
    }
  }

  const entreesFichePdf: DropdownMenuEntry[] = [
    {
      type: 'item',
      item: {
        key: 'fr',
        label: t('regie:oeuvres.gestes.fichePdfLangue.fr'),
        onSelect: () => void ouvrirFichePdf('fr'),
      },
    },
    {
      type: 'item',
      item: {
        key: 'en',
        label: t('regie:oeuvres.gestes.fichePdfLangue.en'),
        onSelect: () => void ouvrirFichePdf('en'),
      },
    },
  ];

  return (
    <div className={styles.page} data-fair={foire ? 'true' : undefined} data-ui="artwork-detail">
      <div className={styles.filAriane} data-ui="artwork-breadcrumb">
        <Link to={`/${galerieSlug}/oeuvres`} className={styles.retour}>
          ← {t('oeuvres:titre')}
        </Link>
        <span className={styles.filAarianeNumero} aria-hidden="true">
          <span className={styles.separateur}>/</span>
          {oeuvre.numeroInventaire}
        </span>
      </div>

      <PlancherOeuvre oeuvreId={oeuvre.id} devise={oeuvre.devise} />
      <div className={styles.entete} data-ui="artwork-header">
        <figure className={styles.planche} data-ui="artwork-image-column">
          {apercu.data ? (
            <Dialog
              size="full"
              closeLabel={t('commun:actions.fermer')}
              title={oeuvre.titre}
              trigger={
                <button
                  type="button"
                  className={styles.imageBouton}
                  aria-label={t('regie:oeuvres.image.agrandir')}
                  data-ui="artwork-image-button"
                >
                  <img className={styles.image} src={apercu.data} alt="" data-ui="artwork-image" />
                  <span className={styles.loupe} aria-hidden="true">
                    <Maximize2 size={16} strokeWidth={1.5} />
                  </span>
                </button>
              }
            >
              <div className={styles.imageAgrandieConteneur}>
                <img className={styles.imageAgrandie} src={apercu.data} alt="" />
              </div>
            </Dialog>
          ) : (
            <div className={styles.imageVide} aria-hidden data-ui="artwork-image-empty">
              <ImageIcon size={28} strokeWidth={1.25} />
            </div>
          )}
        </figure>

        <div className={styles.infosColonne} data-ui="artwork-info">
          {/* Le cartel : ce qu'on lirait au mur, à côté de l'œuvre. */}
          <div className={styles.cartel} data-ui="artwork-cartel">
            <p className={styles.cartelArtiste}>{oeuvre.artisteNom}</p>
            <h1 className={styles.cartelTitre}>{oeuvre.titre}</h1>
            <IndicateurFraicheur depuis={fiche.dataUpdatedAt} />
            {ligneTechnique ? <p className={styles.cartelMeta}>{ligneTechnique}</p> : null}
            {oeuvre.dimensions ? (
              <Dimensions
                className={styles.cartelDimensions}
                height={oeuvre.dimensions.hauteur}
                width={oeuvre.dimensions.largeur}
                {...(oeuvre.dimensions.profondeur === undefined
                  ? {}
                  : { depth: oeuvre.dimensions.profondeur })}
                lang={langue}
              />
            ) : null}
            <InventoryNumber className={styles.cartelInventaire} value={oeuvre.numeroInventaire} />
          </div>

          <div className={styles.vente}>
            <div className={styles.statutLigne} data-ui="artwork-status">
              <PastilleStatut statut={oeuvre.statut} />
              {peutEcrire ? (
                <DropdownMenu
                  align="start"
                  entries={entreesStatut}
                  trigger={
                    <Button variant="secondary" size="sm">
                      {t('regie:oeuvres.statutMenu.titre')}
                      <ChevronDown size={14} strokeWidth={1.5} aria-hidden />
                    </Button>
                  }
                />
              ) : null}
            </div>

            {oeuvre.prixPublicCentimes !== null || fiche.data.localisation ? (
              <div className={styles.prixLigne}>
                {oeuvre.prixPublicCentimes !== null ? (
                  <Price
                    data-ui="artwork-price"
                    className={styles.prixVedette}
                    cents={oeuvre.prixPublicCentimes}
                    lang={langue}
                    currency={oeuvre.devise}
                    size="lg"
                  />
                ) : null}
                {fiche.data.localisation ? (
                  <span className={styles.lieu}>
                    <MapPin size={14} strokeWidth={1.5} aria-hidden />
                    <VisuallyHidden>{t('regie:oeuvres.localisation')} : </VisuallyHidden>
                    {fiche.data.localisation.nom}
                  </span>
                ) : null}
              </div>
            ) : null}
            {oeuvre.statut === 'vendue' && oeuvre.venteSimple ? (
              <p className={styles.aide}>
                {t('regie:oeuvres.venteSimple.mention', {
                  date: formatDate(new Date(oeuvre.venteSimple.venduLe), langue, 'court'),
                })}
              </p>
            ) : null}
          </div>

          <div className={styles.gestes} data-ui="artwork-actions">
            <EntreeNouvelleAffaire
              oeuvreInitiale={{
                id: oeuvre.id,
                titre: oeuvre.titre,
                prixPublicCentimes: oeuvre.prixPublicCentimes,
                editionSuivie:
                  oeuvre.type === 'edition' && oeuvre.tirage?.exemplairesSuivis === true,
              }}
            />
            <BoutonCalculette oeuvreId={oeuvre.id} devise={oeuvre.devise} />
            {galerieALaction(galerie, 'reservations:write') &&
            transitionsPossibles(oeuvre.statut).includes('reservation') ? (
              <Link
                className={classesBouton('secondary', 'sm')}
                to={`/${galerieSlug}/stand/${oeuvre.id}`}
              >
                {t('regie:oeuvres.statutMenu.evenements.reservation')}
              </Link>
            ) : null}
            {galerieALaction(galerie, 'reservations:read') &&
            galerieALaction(galerie, 'reservations:write') &&
            oeuvre.statut === 'reservee' ? (
              <LeverReservationOeuvre galerieSlug={galerieSlug} oeuvreId={oeuvre.id} />
            ) : null}
            {galerieAModule(galerie, 'offres-relances') &&
            galerieALaction(galerie, 'offers:write') ? (
              <Button variant="secondary" size="sm" onClick={() => setOffreOuverte(true)}>
                {t('regie:offres.historique.creerDepuisOeuvre')}
              </Button>
            ) : null}
            {/* Sans facturation, « Marquer vendue » est l'action principale.
                Avec, la vente se facture : la vente simple devient secondaire,
                dans « Plus », et dit qu'aucune facture ne sera émise. */}
            {venteSimplePossible && !facturationOuverte ? (
              <Button variant="accent" size="sm" onClick={() => setVenteSimpleOuverte(true)}>
                {t('regie:oeuvres.venteSimple.action')}
              </Button>
            ) : null}
            <DropdownMenu
              align="start"
              entries={entreesFichePdf}
              trigger={
                <Button variant="secondary" size="sm" loading={fichePdfEnCours}>
                  <FileText size={16} strokeWidth={1.5} aria-hidden />
                  {t('regie:oeuvres.gestes.fichePdf')}
                  <ChevronDown size={14} strokeWidth={1.5} aria-hidden />
                </Button>
              }
            />
            <BoutonEtiquettes oeuvreIds={[oeuvre.id]} />
            {peutArchiver && oeuvre.statut === 'archivee' ? (
              <Button
                variant="secondary"
                size="sm"
                loading={changerStatut.isPending}
                onClick={() =>
                  changerStatut.mutate(
                    {
                      id: oeuvre.id,
                      corps: { statut: 'disponible', raison: t('regie:oeuvres.restaurer.raison') },
                      statutOptimiste: 'disponible',
                    },
                    { onSuccess: () => setRetourGeste(t('regie:oeuvres.restaurer.succes')) },
                  )
                }
              >
                {t('regie:oeuvres.restaurer.action')}
              </Button>
            ) : peutArchiver ? (
              <Button variant="danger" size="sm" onClick={() => setArchivageOuvert(true)}>
                {t('regie:oeuvres.archiver.action')}
              </Button>
            ) : null}
            {(venteSimplePossible && facturationOuverte) || peutAjouterASalle ? (
              <DropdownMenu
                align="start"
                entries={[
                  ...(venteSimplePossible && facturationOuverte
                    ? [
                        {
                          type: 'item' as const,
                          item: {
                            key: 'vente-sans-facture',
                            label: t('regie:oeuvres.venteSimple.actionSecondaire'),
                            onSelect: () => setVenteSimpleOuverte(true),
                          },
                        },
                      ]
                    : []),
                  ...(peutAjouterASalle
                    ? [
                        {
                          type: 'item' as const,
                          item: {
                            key: 'viewing-room',
                            label: t('regie:viewingRooms.ajouterASalle'),
                            onSelect: () => setSalleOuverte(true),
                          },
                        },
                      ]
                    : []),
                ]}
                trigger={
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal size={16} strokeWidth={1.5} aria-hidden />
                    {t('regie:oeuvres.gestes.plus')}
                  </Button>
                }
              />
            ) : null}
            {mouvementsOuverts && (
              <Link
                className={classesBouton('secondary', 'sm')}
                to={`/${galerieSlug}/mouvements?nouveau=1&oeuvre=${oeuvre.id}`}
              >
                {t('regie:oeuvres.gestes.nouveauMouvement')}
              </Link>
            )}
          </div>
          {erreurFichePdf ? (
            <p className={styles.erreur} role="alert">
              {erreurFichePdf}
            </p>
          ) : null}
          <RetourGeste message={retourGeste} />
        </div>
      </div>

      <AvertissementDroitsImage
        medias={fiche.data.medias.filter((m) => m.id === oeuvre.mediaPrincipalId)}
        usages={['impression', 'presse']}
      />
      {galerieAModule(galerie, 'espace-deposant') && galerieALaction(galerie, 'movements:read') && (
        <DepotsRecus oeuvreId={oeuvre.id} />
      )}
      <CatalogageBloc fiche={fiche.data} />
      <EchangesBloc oeuvreId={oeuvre.id} />
      {galerieAModule(galerie, 'espace-collectionneur') &&
        galerieALaction(galerie, 'collector-space:read') && (
          <ConseilsConservation oeuvreId={oeuvre.id} artisteId={oeuvre.artisteId} />
        )}
      {galerieAModule(galerie, 'pipeline-ventes') && galerieALaction(galerie, 'pipeline:write') ? (
        <>
          <ListeAttente oeuvreId={oeuvre.id} ajoutPossible={oeuvre.statut === 'reservee'} />
          {fiche.data.editions.map((e) => (
            <ListeAttente
              key={e.id}
              oeuvreId={oeuvre.id}
              exemplaireId={e.id}
              ajoutPossible={e.statut === 'reservee'}
            />
          ))}
        </>
      ) : null}
      {offreOuverte ? (
        <NouvelleOffreDialog
          open
          onOpenChange={setOffreOuverte}
          oeuvreInitiale={{
            id: oeuvre.id,
            titre: oeuvre.titre,
            prixPublicCentimes: oeuvre.prixPublicCentimes,
            editionSuivie:
              oeuvre.type === 'edition' &&
              (oeuvre.tirage?.exemplairesSuivis === true || Boolean(fiche.data?.editions.length)),
          }}
        />
      ) : null}
      {galerieAModule(galerie, 'offres-relances') && galerieALaction(galerie, 'offers:read') ? (
        <div className={styles.offresOeuvre}>
          <OffresOeuvre galerieSlug={galerieSlug} oeuvreId={oeuvre.id} />
        </div>
      ) : null}
      {salleOuverte && peutAjouterASalle && (
        <AjouterASalleDialog
          galerieSlug={galerieSlug}
          oeuvreId={oeuvre.id}
          onFermer={() => setSalleOuverte(false)}
        />
      )}

      {galerieAModule(galerie, 'reserve-inventaire') &&
        galerieALaction(galerie, 'inventaire:read') && (
          <RangementOeuvre key={oeuvre.id} oeuvreId={oeuvre.id} />
        )}
      {(oeuvre.manques?.length ?? 0) > 0 ? (
        <aside aria-label={t('regie:oeuvres.completude.titre')}>
          <h2>{t('regie:oeuvres.completude.titre')}</h2>
          <ul>
            {oeuvre.manques?.map(({ champ }) => {
              const cible = CHAMPS_A_COMPLETER[champ];
              return (
                <li key={champ}>
                  <Link to={`?onglet=${cible.onglet}&champ=${cible.id}#${cible.id}`}>
                    {t(`regie:oeuvres.completude.champs.${champ}`)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </aside>
      ) : null}
      <div data-ui="artwork-tabs">
        <Tabs
          key={`${params.get('onglet')}/${params.get('champ')}`}
          defaultValue={ongletInitial}
          tabs={dansUnPanneau([
            ...(voitProfils
              ? [
                  {
                    value: 'a-prevenir',
                    label: t('regie:profils.aPrevenir'),
                    content: <APrevenirOeuvre oeuvreId={oeuvre.id} />,
                  },
                ]
              : []),
            {
              value: 'informations',
              label: t('regie:oeuvres.onglets.informations'),
              content: (
                <InformationsTab
                  key={`${oeuvre.id}/${params.get('champ')}`}
                  galerieSlug={galerieSlug}
                  oeuvre={oeuvre}
                  peutEcrire={peutEcrire}
                  editionsOuvertes={editionsOuvertes}
                />
              ),
            },
            {
              value: 'medias',
              label: t('regie:oeuvres.onglets.medias'),
              content: (
                <MediasTab
                  galerieSlug={galerieSlug}
                  oeuvreId={oeuvre.id}
                  medias={fiche.data.medias}
                  mediaPrincipalId={oeuvre.mediaPrincipalId}
                  peutModifierDroits={editionsOuvertes && peutEcrire}
                  peutEcrire={peutEcrireMedia}
                />
              ),
            },
            {
              value: 'editions',
              label: (
                <LibelleOnglet module="editions-provenance">
                  {t('regie:oeuvres.onglets.editions')}
                </LibelleOnglet>
              ),
              content: (
                <EditionsTab
                  galerieSlug={galerieSlug}
                  oeuvreId={oeuvre.id}
                  peutEcrire={peutEcrire && galerieEcritModule(galerie, 'editions-provenance')}
                />
              ),
            },
            ...(editionsOuvertes && galerieALaction(galerie, 'editions:read')
              ? [
                  {
                    value: 'provenance',
                    label: (
                      <LibelleOnglet module="editions-provenance">
                        {t('regie:oeuvres.onglets.provenance')}
                      </LibelleOnglet>
                    ),
                    content: (
                      <ProvenanceTab
                        galerieSlug={galerieSlug}
                        oeuvreId={oeuvre.id}
                        peutEcrire={peutEcrire && galerieALaction(galerie, 'editions:write')}
                        coffreActif={
                          galerieAModule(galerie, 'coffre') &&
                          galerieALaction(galerie, 'documents:read')
                        }
                        peutDeposerPiece={galerieALaction(galerie, 'documents:write')}
                        voitLesCouts={galerieALaction(galerie, 'artworks:read-artist-price')}
                      />
                    ),
                  },
                ]
              : []),
            ...(peutVoirCertificats
              ? [
                  {
                    value: 'certificats',
                    label: (
                      <LibelleOnglet module="documents-prestige">
                        {t('regie:oeuvres.onglets.certificats')}
                      </LibelleOnglet>
                    ),
                    content: (
                      <CertificatsTab
                        key={oeuvre.id}
                        galerieSlug={galerieSlug}
                        oeuvre={oeuvre}
                        peutEmettre={peutEmettreCertificat}
                        peutAnnuler={peutEmettreCertificat && galerie.role === 'direction'}
                        provenanceActive={editionsOuvertes}
                      />
                    ),
                  },
                ]
              : []),
            ...(galerieALaction(galerie, 'diffusion:read') &&
            galerieAModule(galerie, 'artsy-artnet')
              ? [
                  {
                    value: 'diffusion',
                    label: (
                      <LibelleOnglet module="artsy-artnet">
                        {t('regie:oeuvres.onglets.diffusion')}
                      </LibelleOnglet>
                    ),
                    content: (
                      <DiffusionTab
                        key={oeuvre.id}
                        galerieSlug={galerieSlug}
                        oeuvreId={oeuvre.id}
                        peutChoisir={galerieALaction(galerie, 'diffusion:write')}
                      />
                    ),
                  },
                ]
              : []),
            ...(galerieAModule(galerie, 'valeurs-assurance') &&
            galerieALaction(galerie, 'insurance:read')
              ? [
                  {
                    value: 'assurance',
                    label: (
                      <LibelleOnglet module="valeurs-assurance">
                        {t('regie:oeuvres.onglets.assurance')}
                      </LibelleOnglet>
                    ),
                    content: (
                      <AssuranceTab
                        key={oeuvre.id}
                        galerieSlug={galerieSlug}
                        oeuvreId={oeuvre.id}
                        compteId={compte.id}
                        peutEcrire={galerieALaction(galerie, 'insurance:write')}
                      />
                    ),
                  },
                ]
              : []),
            {
              value: 'prix',
              label: t('regie:oeuvres.onglets.prix'),
              content: (
                <PrixTab
                  key={oeuvre.id}
                  galerieSlug={galerieSlug}
                  oeuvre={oeuvre}
                  peutEcrire={peutEcrire}
                />
              ),
            },
            {
              value: 'localisation',
              label: t('regie:oeuvres.onglets.localisation'),
              content: (
                <LocalisationTab
                  mouvementsActifs={
                    galerieAModule(galerie, 'mouvements') &&
                    galerieALaction(galerie, 'movements:read')
                  }
                  key={oeuvre.id}
                  galerieSlug={galerieSlug}
                  oeuvre={oeuvre}
                  peutEcrire={peutEcrire}
                />
              ),
            },
            ...(galerieAModule(galerie, 'location-oeuvres') &&
            galerieALaction(galerie, 'locations:read')
              ? [
                  {
                    value: 'locations',
                    label: (
                      <LibelleOnglet module="location-oeuvres">
                        {t('regie:locations.titre')}
                      </LibelleOnglet>
                    ),
                    content: <LocationsOeuvre galerieSlug={galerieSlug} oeuvreId={oeuvre.id} />,
                  },
                ]
              : []),
            {
              value: 'historique',
              label: t('regie:oeuvres.onglets.historique'),
              content: <HistoriqueTab galerieSlug={galerieSlug} oeuvreId={oeuvre.id} />,
            },
          ])}
        />
      </div>
      {factureOuverte ? (
        <NouvelleFactureDialog
          galerieSlug={galerieSlug}
          open={factureOuverte}
          onOpenChange={setFactureOuverte}
          oeuvre={{ id: oeuvre.id, titre: oeuvre.titre, artisteNom: oeuvre.artisteNom }}
          onCreee={(id) => navigate(`/${galerieSlug}/ventes/${id}`)}
        />
      ) : null}
      {venteSimpleOuverte ? (
        <MarquerVendueDialog
          galerieSlug={galerieSlug}
          oeuvre={oeuvre}
          ouvert={venteSimpleOuverte}
          onOuvertChange={setVenteSimpleOuverte}
          facturationActive={facturationOuverte}
        />
      ) : null}

      <Dialog
        open={correctionOuverte}
        onOpenChange={setCorrectionOuverte}
        title={t('regie:oeuvres.statutMenu.correctionManuelle')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCorrectionOuverte(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              onClick={() => void confirmerCorrection()}
              disabled={!statutVise || raison.trim().length === 0}
              loading={changerStatut.isPending}
            >
              {t('regie:oeuvres.statutMenu.confirmer')}
            </Button>
          </>
        }
      >
        <div className={styles.formulaireCorrection}>
          <Field label={t('regie:oeuvres.statutMenu.statutViseChamp')} htmlFor="statut-vise">
            <Select
              id="statut-vise"
              aria-label={t('regie:oeuvres.statutMenu.statutViseChamp')}
              value={statutVise}
              onValueChange={(valeur) => setStatutVise(valeur as StatutOeuvre)}
              options={STATUTS_OEUVRE.filter(
                (code) => code !== oeuvre.statut && (code !== 'archivee' || peutArchiver),
              ).map((code) => ({
                value: code,
                label: t(`oeuvres:statuts.${code}`),
              }))}
            />
          </Field>
          <Field label={t('regie:oeuvres.statutMenu.raisonChamp')} htmlFor="statut-raison">
            <Textarea
              id="statut-raison"
              required
              rows={3}
              value={raison}
              onChange={(event) => setRaison(event.target.value)}
            />
            <p className={styles.aide}>{t('regie:oeuvres.statutMenu.raisonAide')}</p>
          </Field>
          {erreurStatut ? (
            <p className={styles.erreur} role="alert">
              {erreurStatut}
            </p>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        open={archivageOuvert}
        onOpenChange={setArchivageOuvert}
        title={t('regie:oeuvres.archiver.confirmationTitre')}
        description={t('regie:oeuvres.archiver.confirmationTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setArchivageOuvert(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={archiver.isPending}
              onClick={() => {
                archiver.mutate(
                  { id: oeuvre.id },
                  {
                    onSuccess: () => {
                      setArchivageOuvert(false);
                      navigate(`/${galerieSlug}/oeuvres`);
                    },
                  },
                );
              }}
            >
              {t('regie:oeuvres.archiver.confirmer')}
            </Button>
          </>
        }
      >
        <Badge tone="sold">{t('oeuvres:statuts.archivee')}</Badge>
      </Dialog>
    </div>
  );
}
